/* global $ */

import qs from 'qs';
import debounce from 'lodash/debounce';

import getOptionsFromPrefix from '../helpers/getOptionsFromPrefix';
import pagination from './ListView/Pagination';
import sort from './ListView/Sort';
import columns from './ListView/Columns';

const BODY_CLASS = '.js-list-view-body';
const ACTIONS_CLASS = '.js-list-view-actions';
const PAGINATION_CLASS = '.js-list-view-pagination';
const COUNT_CLASS = '.js-list-view-count';

const inputTextTypes = [
  'text',
  'email',
  'search',
  'number',
  'tel',
];

const inputElements = inputTextTypes.reduce((acc, str) => (
  `${acc}input[type="${str}"],`
), '').replace(/,$/, '');

const ListView = {

  /**
   * Instantiate list view
   * @param {HTMLElement} node
   * @param {Object} options
   * @return {void}
   */
  create(node, options) {
    this.node = node;
    this.$body = $(this.node).find(BODY_CLASS);
    this.$actions = $(this.node).find(ACTIONS_CLASS);
    this.$pagination = $(this.node).find(PAGINATION_CLASS);
    this.$count = $(this.node).find(COUNT_CLASS);

    this.options = getOptionsFromPrefix(options, 'list');

    this.state = this.rehydrateState(this.options.query);

    this.run = () => {
      this.fetchList()
        .then(this.outputList.bind(this));
    };

    this.createPagination();
    this.createSort();
    this.createColumnToggle();
    this.createListeners();
  },

  /**
   * Create event listners for the list view
   * @return {void}
   */
  createListeners() {
    $(this.node).on('click', '.js-list-view-filters, .js-list-view-columns-filter', (e) => {
      e.stopPropagation();
    });

    const debouncedActionChange = debounce(this.actionsChange.bind(this), 250);

    this.$actions.on('keyup', `${inputElements},textarea`, () => {
      debouncedActionChange();
    });

    this.$actions.on('change', 'select, input[type="checkbox"], input[type="radio"]', () => {
      this.actionsChange();
    });
  },

  createPagination() {
    this.pagination = Object.assign({}, pagination);

    this.pagination.initialize(this.node, (type, num) => {
      if (type === 'decrement') {
        this.state.page -= 1;
      } else if (type === 'increment') {
        this.state.page += 1;
      } else {
        this.state.page = num;
      }

      this.run();
    });
  },

  createSort() {
    this.sort = Object.assign({}, sort);

    this.sort.initialize(this.node, (col, direction) => {
      if (direction === 'none') {
        delete this.state.sort;
        delete this.state.sortDirection;
      } else {
        this.state.sort = col;
        this.state.sortDirection = direction;
      }

      this.run();
    });
  },

  createColumnToggle() {
    columns.initialize(this.node);
  },

  /**
   * Rehydrate query string into state
   * @param {String} queryStr
   * @return {Object}
   */
  rehydrateState(queryStr) {
    if (queryStr) {
      const obj = qs.parse(queryStr);

      if (typeof obj.page === 'undefined') {
        obj.page = 1;
      } else {
        obj.page = parseInt(obj.page, 10);
      }

      return obj;
    }

    return {
      page: 1,
    };
  },

  /**
   * Fetch a new list
   * @return {Promise}
   */
  fetchList() {
    const url = `${this.options.url}?${qs.stringify(this.state)}`;

    window.history.replaceState(null, null, url);

    return window.axios.get(url)
      .catch(() => {
        // console.error(err);
      });
  },

  /**
   * Output fetched page to the DOM
   * @param {Object} res
   * @return {void}
   */
  outputList(res) {
    const { data } = res;
    const $html = $(data);

    const $listBody = $html.find(BODY_CLASS);
    const $pagination = $html.find(PAGINATION_CLASS);
    const $count = $html.find(COUNT_CLASS);

    if ($listBody.length) {
      this.$body.html($listBody.html());
    }

    if ($pagination.length) {
      this.$pagination.html($pagination.html());
    }

    if ($count.length) {
      this.$count.html($count.html());
    }
  },

  /**
   * Set the state and fetch a new list after the actions have changed
   * @return {void}
   */
  actionsChange() {
    const formData = qs.parse(this.$actions.serialize());

    formData.page = parseInt(formData.page, 10);

    this.state = formData;

    this.run();
  },

  onNestingChange() {
    const data = this.$nestable.nestable('serialize');
    window.axios.post(this.options.nestableApi, data)
      .catch(() => {
        // console.error(err);
      });
  },
};

export default ListView;
